/**
 * "Joined Fly this week" as a fast carousel on the landing pages (Anand, 02 Nov 26: "Can we add this on the landing
 * page as well for the Local Agent, also on the main landing pages, in fact on all landing pages, as a fast
 * carousel"). The start page's list (StartProofBlock) on one moving row: each business's Google logo or its initial,
 * its name and its city. The title is the start page's, signupRules proofTitle: "Joined Fly this week" with the
 * count while four or more joined in 7 days, the 30 days' title below that.
 *
 * Real switch-ons only (B1's route, read by useJoinedProof). While it reads, when it fails and when it lists nobody,
 * nothing is drawn: no placeholder, no sample names.
 *
 * The row is drawn twice and slides left by one row (translate -50%) every 30 s; each copy is at least as wide as the
 * screen (joined-carousel.css), so the loop shows no gap however few names there are. Hover and keyboard focus pause
 * it. Fewer than four names, or prefers-reduced-motion, show one still row that scrolls by hand. The copy is hidden
 * from assistive tech and its logos carry an empty alt.
 *
 * `tone` picks the light or the dark tokens; a page with tokens of its own (the aurora landing, /home, the Fly Agent
 * pages) maps them in joined-carousel.css, so the strip takes that page's ink, lines and type.
 */
import { useEffect, useId, useState } from 'react';
import { proofTitle } from '@/components/growth-agent/start/signup/signupRules';
import type { SignupProof } from '@/components/growth-agent/start/signup/signupTypes';
import { useJoinedProof } from './useJoinedProof';
import './joined-carousel.css';

type Joined = SignupProof['joined'][number];

/** Below this many names the row stands still: a short list sliding across a wide screen reads as a gap. */
export const MIN_MOVING = 4;
const REDUCE = '(prefers-reduced-motion: reduce)';

/** Whether the visitor asked for less motion, kept current when the setting changes. */
function useStill(): boolean {
  const [still, setStill] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(REDUCE).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(REDUCE);
    if (!mq) return;
    const on = () => setStill(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return still;
}

/** The Google logo, or the first letter in a circle when there is none or it fails to load. The name sits beside it, so the logo is decoration. */
function Mark({ j }: { j: Joined }) {
  const [broken, setBroken] = useState(false);
  if (!j.logo || broken) {
    return (
      <span className="jc-mark is-blank" aria-hidden="true">
        {(j.name.trim()[0] ?? '').toUpperCase()}
      </span>
    );
  }
  return <img className="jc-mark" src={j.logo} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" onError={() => setBroken(true)} />;
}

function Row({ joined, dup }: { joined: Joined[]; dup?: boolean }) {
  return (
    <ul className={dup ? 'jc-dup' : undefined} aria-hidden={dup ? 'true' : undefined}>
      {joined.map((j, i) => (
        <li key={`${j.name}-${i}`} className="jc-card">
          <Mark j={j} />
          <span className="jc-txt">
            <b title={j.name}>{j.name}</b>
            {j.city ? <span>{j.city}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function JoinedCarousel({ tone = 'light', className = '' }: { tone?: 'light' | 'dark'; className?: string }) {
  const id = useId();
  const proof = useJoinedProof();
  const reduced = useStill();
  const joined = (proof?.joined ?? []).filter((j) => typeof j?.name === 'string' && j.name.trim() !== '');
  if (!proof || !joined.length) return null;

  const head = proofTitle(proof);
  const still = reduced || joined.length < MIN_MOVING;
  return (
    <section className={`jc is-${tone}${still ? ' is-still' : ''} ${className}`.trim()} aria-labelledby={id} data-testid="joined-carousel">
      <div className="jc-head">
        <h2 id={id} className="jc-title">
          {head.title}
        </h2>
        {head.count > 0 ? (
          <span className="jc-count">
            {head.count} {head.count === 1 ? 'business' : 'businesses'}
          </span>
        ) : null}
      </div>
      <div className="jc-track" role="region" aria-label="Businesses that joined Fly" tabIndex={0}>
        <div className="jc-in">
          <Row joined={joined} />
          {still ? null : <Row joined={joined} dup />}
        </div>
      </div>
    </section>
  );
}
